import React, { useState, useRef, useEffect } from 'react';
import '../../styles/CarelonRx/ReviewDropdown.css';

interface ReviewDropdownOption {
  id: string;
  label: string;
}

interface ReviewDropdownProps {
  options: ReviewDropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onOpenChange?: (isOpen: boolean) => void;
}

const ReviewDropdown: React.FC<ReviewDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  onOpenChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.id === value);

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!isOpen && triggerRef.current) {
      triggerRef.current.blur();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev < options.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0) {
          handleSelect(options[focusedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  };

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setFocusedIndex(0);
    }
  };

  return (
    <div className={`review-dropdown-wrapper ${isOpen ? 'is-open' : ''}`} ref={dropdownRef}>
      <div
        ref={triggerRef}
        className="review-dropdown-trigger"
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="review-dropdown-value">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <img 
          src={isOpen 
            ? "/assets/carelonRx/images/Arrow Drop Up.svg"
            : "/assets/carelonRx/images/Arrow Drop Down.svg"
          }
          alt=""
          className="review-dropdown-arrow"
        />
      </div>

      {isOpen && (
        <div className="review-dropdown-menu" role="listbox">
          {options.map((option, index) => (
            <div
              key={option.id}
              className={`review-dropdown-option ${focusedIndex === index ? 'focused' : ''} ${value === option.id ? 'selected' : ''}`}
              onClick={() => handleSelect(option.id)}
              onMouseEnter={() => setFocusedIndex(index)}
              role="option"
              aria-selected={value === option.id}
            >
              <span className="review-dropdown-option-text">{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewDropdown;
